import { useId } from "react";
import {
  NO_BRACKET,
  OFFSET_UNITS,
  bracketForPreset,
  bracketWords,
  legsRiskReward,
  presetSummary,
  presetsFor,
  selectPreset,
  selectedPreset,
  symbolRoot,
  type BracketPreset,
  type BracketSide,
  type ContractFacts,
  type OffsetUnit,
} from "@helm/core";
import {
  UNIT_SHORT,
  breakevenText,
  conversionText,
  legBadges,
  riskRewardText,
  trailText,
  type BracketBadge,
} from "@helm/ui";
import { Icon } from "../components/Icon";
import {
  bracketStore,
  openBracketEditor,
  setBracketDisplayUnit,
  useBracketDisplayUnit,
  useBracketDoc,
} from "./bracketStore";
import "./brackets.css";

const EDIT = "__edit";

const UNIT_TITLE: Readonly<Record<OffsetUnit, string>> = {
  ticks: "Show the bracket in ticks",
  points: "Show the bracket in points",
  dollars: "Show the bracket in $ per contract",
};

/** Which side's TP/SL the DOM previews as ghost rows (null = off). */
export type GhostSide = BracketSide | null;

/** The preset remembered for `symbol` (null = "No bracket"). */
export function useSelectedPreset(symbol: string | null): BracketPreset | null {
  return selectedPreset(useBracketDoc(), symbol);
}

/**
 * Tradovate-style bracket-strategy picker (ATM dropdown; SPEC §9.4): "No bracket" + the presets
 * offered for this symbol + "Edit strategies…". The choice is remembered per root in the synced
 * document. The pad quantity follows the preset's quantity (OrderPad); for a different quantity
 * the summary shows what is sent instead (legs scaled, $ legs re-derived — `brackets/order.ts`).
 * Every new entry from the pad, the DOM and the chart goes out with the selected bracket
 * (ADR-0025).
 *
 * ADR-0049: the summary is computed by the very function that builds the order
 * (`bracketForPreset` for this contract and quantity), so what it shows is what is sent — which
 * exits the template places ("TP 20t", "no SL"), breakeven / trail, risk and reward ("no stop",
 * never $0), and for a points / $ template each typed value's ticks with its rounding. The
 * t / pts / $ toggle re-expresses the same ticks for this contract; it changes nothing sent.
 */
export function BracketSelect({
  symbol,
  facts,
  ghost,
  onGhost,
  qty,
  compact = false,
  label = "Bracket",
}: {
  symbol: string | null;
  facts: ContractFacts | null;
  /** The pad's order quantity; flagged when it differs from the preset's legs. */
  qty?: number;
  ghost?: GhostSide;
  onGhost?: (g: GhostSide) => void;
  /** One row (the chart order bar, the DOM header): the long summary goes into the tooltip. */
  compact?: boolean;
  /** The select's visible label ("TP/SL" on the chart order bar, Tradovate's wording). */
  label?: string;
}): React.JSX.Element {
  const id = useId();
  const doc = useBracketDoc();
  const pref = useBracketDisplayUnit();
  const offered = presetsFor(doc, symbol);
  const current = selectedPreset(doc, symbol);
  // Exactly what an entry of this quantity would carry (or why it would be refused).
  const orderQty = qty ?? current?.qty ?? 1;
  const sized = current ? bracketForPreset(current, orderQty, facts) : null;
  const live = facts?.live === true ? facts : null;
  const wanted = pref ?? current?.unit ?? "ticks";
  // Points / $ only from the instrument's reference data, never the CME table.
  const shown: OffsetUnit = live ? wanted : "ticks";

  const badges: BracketBadge[] = [];
  const extras: string[] = [];
  let risk: string | null = null;
  let reward: string | null = null;
  const notes: string[] = [];
  if (sized?.ok) {
    const b = sized.bracket;
    badges.push(...legBadges(b.legs, shown, live));
    if (b.breakeven) extras.push(breakevenText(b.breakeven, shown, live));
    if (b.trail) extras.push(trailText(b.trail, shown, live));
    if (live) {
      const rr = riskRewardText(legsRiskReward(b.legs, live));
      risk = rr.risk;
      reward = rr.reward;
    } else if (b.legs.some((l) => l.stopTicks === null)) {
      risk = "no stop";
    }
    for (const c of sized.conversions) notes.push(`${c.label}: ${conversionText(c, live)}`);
  }
  const rounded = sized?.ok === true && sized.conversions.some((c) => c.result.ok && !c.result.exact);
  const resized = current !== null && qty !== undefined && qty !== current.qty;
  const refused = sized !== null && !sized.ok ? sized.message : null;

  const summaryText = [
    current ? current.name : "No bracket",
    badges.map((x) => x.text).join(" · "),
    extras.join(" · "),
    risk !== null ? `Risk ${risk}` : null,
    reward !== null ? `Reward ${reward}` : null,
    resized && sized?.ok
      ? `qty ${String(qty)}: sends ${sized.bracket.legs.map((l) => String(l.qty)).join("/")} · ${bracketWords(sized.bracket)}`
      : null,
    refused ? `Entries refused: ${refused}` : null,
    ...notes,
  ]
    .filter(Boolean)
    .join("\n");

  const unitToggle = current && (
    <div className="bracket-units segmented-xs" role="radiogroup" aria-label="Show bracket offsets in">
      {OFFSET_UNITS.map((u) => {
        const off = u !== "ticks" && live === null;
        return (
          <button
            key={u}
            type="button"
            role="radio"
            aria-checked={shown === u}
            disabled={off}
            title={off ? "Points and $ need the instrument's reference data (not loaded yet)" : UNIT_TITLE[u]}
            className={`seg seg-xs${shown === u ? " active" : ""}`}
            onClick={() => {
              setBracketDisplayUnit(u);
            }}
          >
            {UNIT_SHORT[u]}
          </button>
        );
      })}
    </div>
  );

  const badgeRow = current && (
    <span className="bracket-badges num" data-testid="bracket-badges">
      {badges.map((x) => (
        <span key={x.text} className={`bracket-badge bracket-badge-${x.kind}`}>
          {x.text}
        </span>
      ))}
      {!compact &&
        extras.map((t) => (
          <span key={t} className="bracket-badge bracket-badge-opt">
            {t}
          </span>
        ))}
      {compact && extras.length > 0 && (
        <span className="bracket-badge bracket-badge-opt" title={extras.join(" · ")}>
          {extras.map((t) => t.split(" ")[0]).join("+")}
        </span>
      )}
      {risk !== null && (
        <span className={`bracket-rr${risk === "no stop" ? " bracket-rr-nostop" : ""}`}>
          Risk {risk}
          {reward !== null && !compact ? ` · Reward ${reward}` : ""}
        </span>
      )}
      {rounded && (
        <span className="bracket-badge bracket-badge-rounded" title={notes.join("\n")}>
          rounded
        </span>
      )}
      {refused && (
        <span className="bracket-badge bracket-badge-refused" role="status">
          {compact ? "Not sendable" : refused}
        </span>
      )}
    </span>
  );

  return (
    <div
      className={compact ? "bracket-select bracket-select-compact" : "bracket-select"}
      data-bracket-unit={current?.unit ?? undefined}
      title={compact ? summaryText : undefined}
    >
      <div className="bracket-select-row">
        <label htmlFor={`${id}-s`} className="bracket-select-label">
          {compact || label.endsWith(":") ? label : `${label}:`}
        </label>
        <select
          id={`${id}-s`}
          className={`select select-sm${current ? "" : " bracket-none"}`}
          value={current?.id ?? NO_BRACKET}
          disabled={symbol === null}
          aria-describedby={`${id}-sum`}
          title={
            compact && current
              ? `${presetSummary(current)} · bracket strategy for entries on this symbol (remembered per symbol)`
              : "Bracket strategy for entries on this symbol (remembered per symbol)"
          }
          onChange={(e) => {
            const v = e.target.value;
            if (v === EDIT) {
              openBracketEditor({ presetId: current?.id, symbol });
              return;
            }
            if (symbol === null) return;
            bracketStore().update((d) => selectPreset(d, symbolRoot(symbol), v === NO_BRACKET ? null : v));
          }}
        >
          {/* The full picker reads "Bracket: none" (owner 2026-10-04: its own label, no extra line). */}
          <option value={NO_BRACKET}>{compact ? "No bracket" : "none"}</option>
          {offered.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
          <option value={EDIT}>Edit strategies…</option>
        </select>
        <button
          type="button"
          className="icon-btn icon-btn-xs"
          aria-label="Edit bracket strategies"
          title="Edit bracket strategies"
          onClick={() => {
            openBracketEditor({ presetId: current?.id, symbol });
          }}
        >
          <Icon name="pencil" size={12} />
        </button>
        {compact && badgeRow}
        {compact && unitToggle}
      </div>
      <div
        id={`${id}-sum`}
        className={compact ? "bracket-summary sr-only" : "bracket-summary"}
        aria-live="polite"
      >
        {compact ? (
          summaryText
        ) : current ? (
          <>
            <div className="bracket-summary-row">
              {badgeRow}
              {unitToggle}
            </div>
            {notes.length > 0 && (
              <ul className="bracket-notes num" aria-label="How the typed values become ticks">
                {notes.map((n) => (
                  <li key={n}>{n}</li>
                ))}
              </ul>
            )}
            {pref !== null && pref !== shown && (
              <div className="muted">Points and $ show once the instrument's reference data loads.</div>
            )}
            {resized && sized?.ok && (
              <span className="bracket-qty-warn num" data-sized="ok">
                qty {qty} ≠ strategy {current.qty}: sends{" "}
                {sized.bracket.legs.map((l) => String(l.qty)).join("/")} · {bracketWords(sized.bracket)}
              </span>
            )}
          </>
        ) : (
          <span className="bracket-none-text sr-only">No bracket: entries go in without TP/SL</span>
        )}
      </div>
      {current && onGhost && (
        <div className="bracket-ghost" role="radiogroup" aria-label="Preview the bracket on the ladder">
          <span className="muted">Ladder preview</span>
          {(
            [
              [null, "Off"],
              ["buy", "Long"],
              ["sell", "Short"],
            ] as const
          ).map(([v, label]) => (
            <button
              key={label}
              type="button"
              role="radio"
              aria-checked={ghost === v}
              className={`seg seg-xs${ghost === v ? " active" : ""}${v ? ` seg-${v}` : ""}`}
              onClick={() => {
                onGhost(v);
              }}
            >
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
